import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

const perks = ["7-day free trial", "Unlimited Mission Watch Scores", "Cancel anytime before billing"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", organization_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Mission Watch — let's set up your first checkup.");
      navigate("/get-started");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-900 mb-10 transition-colors" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="max-w-sm w-full">
          <div className="w-10 h-10 rounded-xl bg-charcoal-900 grid place-items-center mb-6">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900">Start your first checkup</h1>
          <p className="text-charcoal-500 mt-2 text-sm">Create your account, then start your 7-day free trial. No charge today.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-xs font-medium text-charcoal-700">Your name</label>
                <input required value={form.name} onChange={set("name")}
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 bg-white text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  placeholder="Jane Doe" data-testid="register-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-charcoal-700">Organization name</label>
                <input value={form.organization_name} onChange={set("organization_name")}
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 bg-white text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  placeholder="Hope Foundation" data-testid="register-org" />
              </div>
              <div>
                <label className="text-xs font-medium text-charcoal-700">Email</label>
                <input type="email" required value={form.email} onChange={set("email")}
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 bg-white text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  placeholder="you@nonprofit.org" data-testid="register-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-charcoal-700">Password</label>
                <input type="password" required minLength={6} value={form.password} onChange={set("password")}
                  className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 bg-white text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  placeholder="At least 6 characters" data-testid="register-password" />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-11 rounded-xl bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors disabled:opacity-60"
              data-testid="register-submit">
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>
          <p className="text-sm text-charcoal-500 mt-6">
            Already have an account? <Link to="/login" className="text-brand font-medium hover:underline" data-testid="go-login">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex relative bg-charcoal-900 tech-grid overflow-hidden flex-col justify-center px-16">
        <p className="text-emerald-400 font-mono text-xs tracking-[0.2em] uppercase mb-6">What you get</p>
        <ul className="space-y-4">
          {perks.map((p) => (
            <li key={p} className="flex items-center gap-3 text-white text-lg">
              <span className="w-6 h-6 rounded-full bg-emerald-500/15 grid place-items-center">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
              </span>
              {p}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
